import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { ShoppingBag, FileText, Loader2, TrendingUp, DollarSign } from "lucide-react";

const PurchasesTab = ({ user }) => {
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPurchases();
  }, []);

  const fetchPurchases = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/customer/purchases`);
      setPurchases(response.data);
    } catch (error) {
      console.error("Failed to fetch purchases:", error);
      toast.error("Failed to load purchases");
    } finally {
      setLoading(false);
    }
  };

  const getCurrencySymbol = (currency) => {
    const symbols = { EUR: "€", USD: "$", GBP: "£" };
    return symbols[currency] || currency;
  };

  const handleDownloadPDF = async (purchaseId, bundleName) => {
    try {
      const response = await axios.get(`${API}/customer/esg-report/${purchaseId}/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ESG_Report_${bundleName.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('ESG Report downloaded successfully');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Failed to download ESG report');
    }
  };

  const getPaymentStatusBadge = (status) => {
    const statusConfig = {
      completed: { label: "Completed", className: "bg-green-600 text-white" },
      pending: { label: "Pending", className: "bg-yellow-600 text-white" },
      processing: { label: "Processing", className: "bg-blue-600 text-white" },
      failed: { label: "Failed", className: "bg-red-600 text-white" }
    };
    
    const config = statusConfig[status] || statusConfig.completed;
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const getBorderColor = (status) => {
    const colors = {
      completed: "border-l-green-500",
      pending: "border-l-yellow-500",
      processing: "border-l-blue-500",
      failed: "border-l-red-500"
    };
    return colors[status] || colors.completed;
  };

  const totalSpent = purchases.reduce((sum, p) => sum + p.total_price, 0);
  const totalTokens = purchases.reduce((sum, p) => sum + p.token_count, 0);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-gold-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Purchases</p>
                <p className="text-2xl font-bold text-gold-700">{purchases.length}</p>
              </div>
              <ShoppingBag className="w-8 h-8 text-gold-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Tokens</p>
                <p className="text-2xl font-bold text-blue-700">{totalTokens}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Spent</p>
                <p className="text-2xl font-bold text-green-700">
                  {purchases.length > 0 ? getCurrencySymbol(purchases[0].currency) : "€"}
                  {totalSpent.toFixed(2)}
                </p>
              </div>
              <DollarSign className="w-8 h-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Purchase List */}
      <div>
        <h3 className="text-xl font-bold text-gray-800 mb-4">Purchase History</h3>
        {purchases.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <ShoppingBag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">No purchases yet</p>
              <p className="text-sm text-gray-400 mt-2">Browse the marketplace to purchase token bundles</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {purchases.map((purchase) => (
              <Card key={purchase.id} className={`border-l-4 ${getBorderColor(purchase.payment_status)}`}>
                <CardContent className="p-6">
                  <div className="flex justify-between items-start">
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold text-lg text-gray-800">{purchase.bundle_name}</h4>
                        {getPaymentStatusBadge(purchase.payment_status)}
                        <Badge variant="outline">{purchase.token_count} tokens</Badge>
                      </div>
                      <div className="text-sm text-gray-600 space-y-1">
                        <p>
                          <strong>Amount Paid:</strong> {getCurrencySymbol(purchase.currency)}{purchase.total_price.toFixed(2)}
                        </p>
                        <p>
                          <strong>Price per Token:</strong> {getCurrencySymbol(purchase.currency)}{purchase.price_per_token.toFixed(2)}
                        </p>
                        <p>
                          <strong>Payment Method:</strong> {purchase.payment_method === 'fake' ? 'Test Payment' : purchase.payment_method}
                        </p>
                        <p className="text-xs text-gray-500">
                          Purchased: {new Date(purchase.purchased_at).toLocaleString()}
                        </p>
                      </div>
                      {purchase.payment_status === 'completed' && (
                        <div className="mt-3">
                          <Button
                            onClick={() => handleDownloadPDF(purchase.id, purchase.bundle_name)}
                            className="!bg-blue-600 hover:!bg-blue-700 !text-white"
                          >
                            <FileText className="w-4 h-4 mr-2" />
                            Download PDF of ESG Report
                          </Button>
                        </div>
                      )}
                      {purchase.payment_status === 'pending' && (
                        <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded">
                          <p className="text-sm text-yellow-900 flex items-center gap-2">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <strong>Payment Pending</strong>
                          </p>
                          <p className="text-xs text-yellow-700 mt-1">
                            Your payment is being processed
                          </p>
                        </div>
                      )}
                      {purchase.payment_status === 'failed' && (
                        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded">
                          <p className="text-sm text-red-900">
                            <strong>Payment Failed</strong>
                          </p>
                          <p className="text-xs text-red-700 mt-1">
                            Please contact support or try again
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PurchasesTab;
